/**
 * Server-side thread search.
 *
 * Calls GET /api/auth/threads/search?q=...&limit=...
 * Debounces queries client-side; aborts in-flight requests if the query changes.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const API = '/api/auth';
const TOKEN_KEY = 'foreman-auth-token';
const DEBOUNCE_MS = 300;
const MIN_QUERY_LEN = 2;

export type SearchMatchType = 'name' | 'email_subject' | 'email_body';

export interface ThreadSearchResult {
  sessionKey: string;
  name: string | null;
  owner: string;
  sharedWith: string[] | '*';
  matchType: SearchMatchType;
  /** Already includes **bold** markup around the matched substring. */
  snippet: string;
  updatedAt: number;
}

export interface UseThreadSearchReturn {
  query: string;
  setQuery: (q: string) => void;
  results: ThreadSearchResult[];
  totalMatches: number;
  loading: boolean;
  error: string | null;
  /** True when query is set and >= MIN_QUERY_LEN. UI uses this to swap views. */
  active: boolean;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function useThreadSearch(): UseThreadSearchReturn {
  const [query, setQueryState] = useState('');
  const [results, setResults] = useState<ThreadSearchResult[]>([]);
  const [totalMatches, setTotalMatches] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const setQuery = useCallback((q: string) => {
    setQueryState(q);
  }, []);

  // Trim to decide whether the search is "active" — but query state stores raw
  // input so the input is uncontrolled-feeling.
  const trimmed = query.trim();
  const active = trimmed.length >= MIN_QUERY_LEN;

  useEffect(() => {
    // Cancel pending debounce + any in-flight request.
    if (timerRef.current) clearTimeout(timerRef.current);
    if (abortRef.current) abortRef.current.abort();

    if (!active) {
      setResults([]);
      setTotalMatches(0);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    timerRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;
      const url = `${API}/threads/search?q=${encodeURIComponent(trimmed)}&limit=30`;
      fetch(url, { headers: authHeaders(), signal: controller.signal })
        .then(async (res) => {
          if (!res.ok) {
            const data = await res.json().catch(() => ({ error: 'Search failed' }));
            throw new Error(data.error || `HTTP ${res.status}`);
          }
          return res.json();
        })
        .then((data) => {
          setResults(data.results || []);
          setTotalMatches(data.totalMatches || 0);
          setLoading(false);
        })
        .catch((err) => {
          // AbortError is expected on rapid re-typing
          if (err.name === 'AbortError') return;
          console.error('[useThreadSearch] failed:', err);
          setError(err.message || 'Search failed');
          setResults([]);
          setTotalMatches(0);
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [trimmed, active]);

  return { query, setQuery, results, totalMatches, loading, error, active };
}
